import Enum from '/@/modules/core/app/helpers/Enum';
import Lang from '/@/modules/core/app/services/Lang';
import { PredefinedCountry } from '/@/modules/predefined/enums/PredefinedCountry';
import { EstateStatusKey } from '@/modules/estate/constants/EstateStatusKey';
import { Color } from '@/modules/core/app/enums/Color';

export class EstateStatus extends Enum {
  key!: EstateStatusKey;
  value!: string;
  color!: string;

  static REFERRAL_PRE_FILLING: EstateStatus = {
    key: EstateStatusKey.REFERRAL_PRE_FILLING,
    get value() {
      return Lang.t('estate.referral-pre-filling');
    },
    color: '#ff7003'
  };

  static ASSET_ASSESSMENT: EstateStatus = {
    key: EstateStatusKey.ASSET_ASSESSMENT,
    get value() {
      return Lang.t('estate.asset-assessment');
    },
    color: '#ce2584'
  };

  static AWAITING_CLEARANCE: EstateStatus = {
    key: EstateStatusKey.AWAITING_CLEARANCE,
    get value() {
      return Lang.t('estate.awaiting-clearance');
    },
    color: '#0231e8'
  };

  static CHURNED: EstateStatus = {
    key: EstateStatusKey.CHURNED,
    get value() {
      return Lang.t('estate.churned');
    },
    color: '#800000'
  };

  static CLIENT_REVIEW: EstateStatus = {
    key: EstateStatusKey.CLIENT_REVIEW,
    get value() {
      return Lang.t('estate.client-review');
    },
    color: '#cc136e'
  };

  static AWAITING_T3_NOA: EstateStatus = {
    key: EstateStatusKey.AWAITING_T3_NOA,
    get value() {
      return Lang.t('estate.awaiting-t3-noa');
    },
    color: '#7fffd4'
  };

  static CLEARANCE_CERTIFICATE_PREP: EstateStatus = {
    key: EstateStatusKey.CLEARANCE_CERTIFICATE_PREP,
    get value() {
      return Lang.t('estate.clearance-certificate-prep');
    },
    color: '#ff6347'
  };

  static CLOSING_COMMUNICATION: EstateStatus = {
    key: EstateStatusKey.CLOSING_COMMUNICATION,
    get value() {
      return Lang.t('estate.closing-communication');
    },
    color: '#c8352f'
  };

  static CLOSING_LETTER_PREPARATION: EstateStatus = {
    key: EstateStatusKey.CLOSING_LETTER_PREPARATION,
    get value() {
      return Lang.t('estate.closing-letter-preparation');
    },
    color: '#6a1f35'
  };

  static COMPLETED: EstateStatus = {
    key: EstateStatusKey.COMPLETED,
    get value() {
      return Lang.t('estate.completed-status');
    },
    color: '#6bc950'
  };

  static COMPLETED_AR_CHURNED: EstateStatus = {
    key: EstateStatusKey.COMPLETED_AR_CHURNED,
    get value() {
      return Lang.t('estate.completed-ar-churned');
    },
    color: '#6bc950'
  };

  static EARLY_CLOSURE: EstateStatus = {
    key: EstateStatusKey.EARLY_CLOSURE,
    get value() {
      return Lang.t('estate.early-closure');
    },
    color: '#0231e8'
  };

  static FINAL_ACCOUNTING: EstateStatus = {
    key: EstateStatusKey.FINAL_ACCOUNTING,
    get value() {
      return Lang.t('estate.final-accounting');
    },
    color: '#e65100'
  };

  static FINAL_DISTRIBUTION: EstateStatus = {
    key: EstateStatusKey.FINAL_DISTRIBUTION,
    get value() {
      return Lang.t('estate.final-distribution');
    },
    color: '#19ca8f'
  };

  static FINAL_DISCHARGE: EstateStatus = {
    key: EstateStatusKey.FINAL_DISCHARGE,
    get value() {
      return Lang.t('estate.final-discharge');
    },
    color: '#0231e8'
  };

  static FIRST_NOTIFICATIONS: EstateStatus = {
    key: EstateStatusKey.FIRST_NOTIFICATIONS,
    get value() {
      return Lang.t('estate.first-notifications');
    },
    color: '#e50000'
  };

  static INTERIM_DISTRIBUTION: EstateStatus = {
    key: EstateStatusKey.INTERIM_DISTRIBUTION,
    get value() {
      return Lang.t('estate.interim-distribution');
    },
    color: '#2ecd6f'
  };

  static INVENTORY: EstateStatus = {
    key: EstateStatusKey.INVENTORY,
    get value() {
      return Lang.t('estate.inventory');
    },
    color: '#81b1ff'
  };

  static LIQUIDATION: EstateStatus = {
    key: EstateStatusKey.LIQUIDATION,
    get value() {
      return Lang.t('estate.liquidation');
    },
    color: '#3082b7'
  };

  static MAIL_FOLDER: EstateStatus = {
    key: EstateStatusKey.MAIL_FOLDER,
    get value() {
      return Lang.t('estate.mail-folder');
    },
    color: '#667684'
  };

  static NEW: EstateStatus = {
    key: EstateStatusKey.NEW,
    get value() {
      return Lang.t('estate.new');
    },
    color: '#ff7003'
  };

  static PETITION_PREPARATION: EstateStatus = {
    key: EstateStatusKey.PETITION_PREPARATION,
    get value() {
      return Lang.t('estate.petition-preparation');
    },
    color: '#dcca2c'
  };

  static PETITION_PREPARED_WAITING_ON_CLIENT: EstateStatus = {
    key: EstateStatusKey.PETITION_PREPARED_WAITING_ON_CLIENT,
    get value() {
      return Lang.t('estate.petition-preparation-waiting-on-client');
    },
    color: '#abb51d'
  };

  static PETITION_FILED_WAITING: EstateStatus = {
    key: EstateStatusKey.PETITION_FILED_WAITING,
    get value() {
      return Lang.t('estate.petition-filed-waiting');
    },
    color: '#a875ff'
  };

  static PLAN_MY_ESTATE: EstateStatus = {
    key: EstateStatusKey.PLAN_MY_ESTATE,
    get value() {
      return Lang.t('estate.plan-my-estate');
    },
    color: '#667684'
  };

  static REVISING_PETITION: EstateStatus = {
    key: EstateStatusKey.REVISING_PETITION,
    get value() {
      return Lang.t('estate.revising-petition');
    },
    color: '#af7e2e'
  };

  static SECOND_NOTIFICATIONS: EstateStatus = {
    key: EstateStatusKey.SECOND_NOTIFICATIONS,
    get value() {
      return Lang.t('estate.second-notifications');
    },
    color: '#e65100'
  };

  static WILL_DRAFTING_AND_PREP: EstateStatus = {
    key: EstateStatusKey.WILL_DRAFTING_AND_PREP,
    get value() {
      return Lang.t('estate.will-prep-internal');
    },
    color: '#fcaa59'
  };

  static WILL_PREP_EXTERNAL: EstateStatus = {
    key: EstateStatusKey.WILL_PREP_EXTERNAL,
    get value() {
      return Lang.t('estate.will-prep-external');
    },
    color: '#4b7658'
  };

  static SMALL_ESTATE: EstateStatus = {
    key: EstateStatusKey.SMALL_ESTATE,
    get value() {
      return Lang.t('estate.small-estate');
    },
    color: '#c059fc'
  };

  static ESSENTIAL_POST_PROBATE: EstateStatus = {
    key: EstateStatusKey.ESSENTIAL_POST_PROBATE,
    get value() {
      return Lang.t('estate.essential-post-probate');
    },
    color: '#009688'
  };

  static PLUS_POST_PROBATE: EstateStatus = {
    key: EstateStatusKey.PLUS_POST_PROBATE,
    get value() {
      return Lang.t('estate.plus-post-probate');
    },
    color: '#fde047'
  };

  static INTAKE: EstateStatus = {
    key: EstateStatusKey.INTAKE,
    get value() {
      return Lang.t('estate.intake');
    },
    color: '#009688'
  };

  static DISCOVERY: EstateStatus = {
    key: EstateStatusKey.DISCOVERY,
    get value() {
      return Lang.t('estate.discovery');
    },
    color: '#4CAF50'
  };

  static GUIDANCE: EstateStatus = {
    key: EstateStatusKey.GUIDANCE,
    get value() {
      return Lang.t('estate.guidance');
    },
    color: '#8BC34A'
  };

  static DECISIONS: EstateStatus = {
    key: EstateStatusKey.DECISIONS,
    get value() {
      return Lang.t('estate.decisions');
    },
    color: '#CDDC39'
  };

  static SIGNATURES: EstateStatus = {
    key: EstateStatusKey.SIGNATURES,
    get value() {
      return Lang.t('estate.signatures');
    },
    color: '#FFC107'
  };

  static UPLOADS: EstateStatus = {
    key: EstateStatusKey.UPLOADS,
    get value() {
      return Lang.t('estate.uploads');
    },
    color: '#FF9800'
  };

  static AR_ASSESSMENT: EstateStatus = {
    key: EstateStatusKey.AR_ASSESSMENT,
    get value() {
      return Lang.t('estate.ar-assessment');
    },
    color: '#FF5722'
  };

  static AR_GUIDANCE: EstateStatus = {
    key: EstateStatusKey.AR_GUIDANCE,
    get value() {
      return Lang.t('estate.ar-guidance');
    },
    color: '#795548'
  };

  static AR_DECISIONS: EstateStatus = {
    key: EstateStatusKey.AR_DECISIONS,
    get value() {
      return Lang.t('estate.ar-decisions');
    },
    color: '#9E9E9E'
  };

  static AR_WILL_PREP: EstateStatus = {
    key: EstateStatusKey.AR_WILL_PREP,
    get value() {
      return Lang.t('estate.ar-will-prep');
    },
    color: '#607D8B'
  };

  static AR_CLIENT_REVIEW: EstateStatus = {
    key: EstateStatusKey.AR_CLIENT_REVIEW,
    get value() {
      return Lang.t('estate.ar-client-review');
    },
    color: '#673AB7'
  };

  static AR_PENDING_SIGNATURES: EstateStatus = {
    key: EstateStatusKey.AR_PENDING_SIGNATURES,
    get value() {
      return Lang.t('estate.ar-pending-signatures');
    },
    color: '#3F51B5'
  };

  static AR_UPLOADS: EstateStatus = {
    key: EstateStatusKey.AR_UPLOADS,
    get value() {
      return Lang.t('estate.ar-uploads');
    },
    color: '#3F51B5'
  };

  static AR_AFFIDAVIT_OF_EXECUTION: EstateStatus = {
    key: EstateStatusKey.AR_AFFIDAVIT_OF_EXECUTION,
    get value() {
      return Lang.t('estate.ar-affidavit-of-execution');
    },
    color: '#DF5722'
  };

  static AR_WAITING_ON_ORIGINALS: EstateStatus = {
    key: EstateStatusKey.AR_WAITING_ON_ORIGINALS,
    get value() {
      return Lang.t('estate.ar-waiting-on-originals');
    },
    color: '#C57222'
  };

  static NON_RESPONSIVE: EstateStatus = {
    key: EstateStatusKey.NON_RESPONSIVE,
    get value() {
      return Lang.t('estate.non-responsive');
    },
    color: '#FF5722'
  };

  static REVISING_DOCUMENTS: EstateStatus = {
    key: EstateStatusKey.REVISING_DOCUMENTS,
    get value() {
      return Lang.t('estate.revising-documents');
    },
    color: '#795548'
  };

  static DISENGAGED: EstateStatus = {
    key: EstateStatusKey.DISENGAGED,
    get value() {
      return Lang.t('estate.disengaged');
    },
    color: '#9E9E9E'
  };

  static NBA_INTAKE: EstateStatus = {
    key: EstateStatusKey.NBA_INTAKE,
    get value() {
      return Lang.t('estate.nba-intake');
    },
    color: '#AE4949'
  };

  static NBA_PENDING_SUBMISSION: EstateStatus = {
    key: EstateStatusKey.NBA_PENDING_SUBMISSION,
    get value() {
      return Lang.t('estate.nba-pending-submission');
    },
    color: '#2F5EA1'
  };

  static NBA_SUBMITTED: EstateStatus = {
    key: EstateStatusKey.NBA_SUBMITTED,
    get value() {
      return Lang.t('estate.nba-submitted');
    },
    color: '#7CC54D'
  };

  static NBA_CONDITIONAL: EstateStatus = {
    key: EstateStatusKey.NBA_CONDITIONAL,
    get value() {
      return Lang.t('estate.nba-conditional');
    },
    color: '#512E68'
  };

  static POA_MANAGEMENT_YEAR_2: EstateStatus = {
    key: EstateStatusKey.POA_MANAGEMENT_YEAR_2,
    get value() {
      return Lang.t('estate.poa-management-year-2');
    },
    color: '#7B68EE'
  };

  static POA_MANAGEMENT_YEAR_3: EstateStatus = {
    key: EstateStatusKey.POA_MANAGEMENT_YEAR_3,
    get value() {
      return Lang.t('estate.poa-management-year-3');
    },
    color: '#6A5ACD'
  };

  static POA_MANAGEMENT_YEAR_4: EstateStatus = {
    key: EstateStatusKey.POA_MANAGEMENT_YEAR_4,
    get value() {
      return Lang.t('estate.poa-management-year-4');
    },
    color: '#483D8B'
  };

  static POA_MANAGEMENT_YEAR_5: EstateStatus = {
    key: EstateStatusKey.POA_MANAGEMENT_YEAR_5,
    get value() {
      return Lang.t('estate.poa-management-year-5');
    },
    color: '#4B0082'
  };

  static AFFIDAVIT_OF_EXECUTION: EstateStatus = {
    key: EstateStatusKey.AFFIDAVIT_OF_EXECUTION,
    get value() {
      return Lang.t('estate.affidavit-of-execution');
    },
    color: '#975A97'
  };

  static WAITING_ON_ORIGINALS: EstateStatus = {
    key: EstateStatusKey.WAITING_ON_ORIGINALS,
    get value() {
      return Lang.t('estate.waiting-on-originals');
    },
    color: '#FFB347'
  };

  static ON_HOLD: EstateStatus = {
    key: EstateStatusKey.ON_HOLD,
    get value() {
      return Lang.t('estate.on-hold');
    },
    color: '#778899'
  };

  static PEER_REVIEW: EstateStatus = {
    key: EstateStatusKey.PEER_REVIEW,
    get value() {
      return Lang.t('estate.peer-review');
    },
    color: '#8E24AA'
  };

  static AUDIT: EstateStatus = {
    key: EstateStatusKey.AUDIT,
    get value() {
      return Lang.t('estate.audit');
    },
    color: '#00897B'
  };

  static AR_CONTACT: EstateStatus = {
    key: EstateStatusKey.AR_CONTACT,
    get value() {
      return Lang.t('estate.ar-contact');
    },
    color: '#D84315'
  };

  static AR_SS_COMPLETED: EstateStatus = {
    key: EstateStatusKey.AR_SS_COMPLETED,
    get value() {
      return Lang.t('estate.ar-ss-completed');
    },
    color: Color.FRESH_GREEN
  };

  static AR_NO_CHANGES: EstateStatus = {
    key: EstateStatusKey.AR_NO_CHANGES,
    get value() {
      return Lang.t('estate.ar-no-changes');
    },
    color: Color.EMERALD_GREEN
  };

  static AR_GUIDANCE_CSM: EstateStatus = {
    key: EstateStatusKey.AR_GUIDANCE_CSM,
    get value() {
      return Lang.t('estate.ar-guidance-csm');
    },
    color: Color.PUMPKIN_ORANGE
  };

  static AR_GUIDANCE_EPA: EstateStatus = {
    key: EstateStatusKey.AR_GUIDANCE_EPA,
    get value() {
      return Lang.t('estate.ar-guidance-epa');
    },
    color: Color.TANGERINE
  };

  static COMPLETED_ACTING_FALL_IN: EstateStatus = {
    key: EstateStatusKey.COMPLETED_ACTING_FALL_IN,
    get value() {
      return Lang.t('estate.completed-acting-fall-in');
    },
    color: Color.FRESH_GREEN
  };

  static COMPLETED_DECEASED_NOT_ACTING: EstateStatus = {
    key: EstateStatusKey.COMPLETED_DECEASED_NOT_ACTING,
    get value() {
      return Lang.t('estate.completed-deceased-not-acting');
    },
    color: Color.SLATE_GRAY
  };

  static SUPPORT_MEETING_BOOKED: EstateStatus = {
    key: EstateStatusKey.SUPPORT_MEETING_BOOKED,
    get value() {
      return Lang.t('estate.support-meeting-booked');
    },
    color: Color.MUTED_AQUA
  };

  static SUPPORT_MEETING_COMPLETED: EstateStatus = {
    key: EstateStatusKey.SUPPORT_MEETING_COMPLETED,
    get value() {
      return Lang.t('estate.support-meeting-completed');
    },
    color: Color.OCEAN_BLUE
  };

  static getArTagActiveStatusKeys(): EstateStatusKey[] {
    return [
      EstateStatusKey.AR_ASSESSMENT,
      EstateStatusKey.AR_CONTACT,
      EstateStatusKey.AR_GUIDANCE,
      EstateStatusKey.AR_GUIDANCE_CSM,
      EstateStatusKey.AR_GUIDANCE_EPA,
      EstateStatusKey.AR_DECISIONS,
      EstateStatusKey.AR_WILL_PREP,
      EstateStatusKey.AR_CLIENT_REVIEW
    ];
  }

  static getClosedStatuses(): EstateStatus[] {
    return [
      EstateStatus.CHURNED,
      EstateStatus.EARLY_CLOSURE,
      EstateStatus.COMPLETED,
      EstateStatus.COMPLETED_ACTING_FALL_IN,
      EstateStatus.COMPLETED_DECEASED_NOT_ACTING,
      EstateStatus.COMPLETED_AR_CHURNED,
      EstateStatus.DISENGAGED
    ];
  }

  static getClosedPlanningStatuses(): EstateStatus[] {
    return [
      EstateStatus.CHURNED,
      EstateStatus.EARLY_CLOSURE,
      EstateStatus.COMPLETED,
      EstateStatus.COMPLETED_ACTING_FALL_IN,
      EstateStatus.COMPLETED_DECEASED_NOT_ACTING,
      EstateStatus.COMPLETED_AR_CHURNED,
      EstateStatus.DISENGAGED
    ];
  }

  static getClosedSettlementStatuses(): EstateStatus[] {
    return [
      EstateStatus.CHURNED,
      EstateStatus.EARLY_CLOSURE,
      EstateStatus.COMPLETED,
      EstateStatus.DISENGAGED
    ];
  }

  static getClosedStatusKeys(): EstateStatusKey[] {
    return this.getClosedStatuses().map((status: EstateStatus) => status.key);
  }

  static getOpenedStatuses(): EstateStatus[] {
    return this.getValues<EstateStatus>().filter(
      (status: EstateStatus) => !EstateStatus.getClosedStatuses().includes(status)
    );
  }

  static getAllActiveSettlementStatusesByCountry(countryId: number): EstateStatus[] | undefined {
    return EstateStatus.getActiveSettlementStatusesByCountry(countryId)?.concat(
      EstateStatus.getClosedStatuses()
    );
  }

  static getActiveSettlementStatusesByCountry(countryId: number): EstateStatus[] | undefined {
    switch (countryId) {
      case PredefinedCountry.CANADA.id:
        return EstateStatus.getActiveCanadianSettlementStatuses();
      case PredefinedCountry.UNITED_STATES.id:
        return EstateStatus.getActiveAmericanSettlementStatuses();
      default:
        return undefined;
    }
  }

  static getActiveCanadianSettlementStatuses(): EstateStatus[] {
    return [
      EstateStatus.NEW,
      EstateStatus.NBA_INTAKE,
      EstateStatus.NBA_PENDING_SUBMISSION,
      EstateStatus.NBA_SUBMITTED,
      EstateStatus.NBA_CONDITIONAL,
      EstateStatus.FIRST_NOTIFICATIONS,
      EstateStatus.INVENTORY,
      EstateStatus.PETITION_PREPARATION,
      EstateStatus.PETITION_PREPARED_WAITING_ON_CLIENT,
      EstateStatus.PETITION_FILED_WAITING,
      EstateStatus.REVISING_PETITION,
      EstateStatus.LIQUIDATION,
      EstateStatus.FINAL_ACCOUNTING,
      EstateStatus.INTERIM_DISTRIBUTION,
      EstateStatus.AWAITING_T3_NOA,
      EstateStatus.CLEARANCE_CERTIFICATE_PREP,
      EstateStatus.AWAITING_CLEARANCE,
      EstateStatus.FINAL_DISTRIBUTION,
      EstateStatus.CLOSING_COMMUNICATION,
      EstateStatus.CLOSING_LETTER_PREPARATION,
      EstateStatus.POA_MANAGEMENT_YEAR_2,
      EstateStatus.POA_MANAGEMENT_YEAR_3,
      EstateStatus.POA_MANAGEMENT_YEAR_4,
      EstateStatus.POA_MANAGEMENT_YEAR_5,
      EstateStatus.ESSENTIAL_POST_PROBATE,
      EstateStatus.PLUS_POST_PROBATE
    ];
  }

  static getActiveCanadianSettlementPostPetitionPreparationStatuses(): EstateStatus[] {
    return [
      EstateStatus.PETITION_FILED_WAITING,
      EstateStatus.REVISING_PETITION,
      EstateStatus.LIQUIDATION,
      EstateStatus.FINAL_ACCOUNTING,
      EstateStatus.INTERIM_DISTRIBUTION,
      EstateStatus.AWAITING_CLEARANCE,
      EstateStatus.FINAL_DISTRIBUTION,
      EstateStatus.CLOSING_COMMUNICATION,
      EstateStatus.CLOSING_LETTER_PREPARATION
    ];
  }

  static getActiveAmericanSettlementStatuses(): EstateStatus[] {
    return [
      EstateStatus.NEW,
      EstateStatus.FIRST_NOTIFICATIONS,
      EstateStatus.ASSET_ASSESSMENT,
      EstateStatus.PETITION_PREPARATION,
      EstateStatus.PETITION_PREPARED_WAITING_ON_CLIENT,
      EstateStatus.PETITION_FILED_WAITING,
      EstateStatus.REVISING_PETITION,
      EstateStatus.SECOND_NOTIFICATIONS,
      EstateStatus.INVENTORY,
      EstateStatus.LIQUIDATION,
      EstateStatus.FINAL_ACCOUNTING,
      EstateStatus.INTERIM_DISTRIBUTION,
      EstateStatus.FINAL_DISCHARGE,
      EstateStatus.FINAL_DISTRIBUTION,
      EstateStatus.CLOSING_COMMUNICATION,
      EstateStatus.CLOSING_LETTER_PREPARATION,
      EstateStatus.POA_MANAGEMENT_YEAR_2,
      EstateStatus.POA_MANAGEMENT_YEAR_3,
      EstateStatus.POA_MANAGEMENT_YEAR_4,
      EstateStatus.POA_MANAGEMENT_YEAR_5,
      EstateStatus.SMALL_ESTATE,
      EstateStatus.ESSENTIAL_POST_PROBATE,
      EstateStatus.PLUS_POST_PROBATE
    ];
  }

  static getActivePlanningStatuses(): EstateStatus[] {
    return [
      EstateStatus.NEW,
      EstateStatus.INTAKE,
      EstateStatus.SUPPORT_MEETING_BOOKED,
      EstateStatus.SUPPORT_MEETING_COMPLETED,
      EstateStatus.DISCOVERY,
      EstateStatus.GUIDANCE,
      EstateStatus.DECISIONS,
      EstateStatus.WILL_DRAFTING_AND_PREP,
      EstateStatus.WILL_PREP_EXTERNAL,
      EstateStatus.PEER_REVIEW,
      EstateStatus.CLIENT_REVIEW,
      EstateStatus.REVISING_DOCUMENTS,
      EstateStatus.SIGNATURES,
      EstateStatus.UPLOADS,
      EstateStatus.MAIL_FOLDER,
      EstateStatus.AFFIDAVIT_OF_EXECUTION,
      EstateStatus.WAITING_ON_ORIGINALS,
      EstateStatus.NON_RESPONSIVE,
      EstateStatus.AUDIT,
      EstateStatus.ON_HOLD,
      EstateStatus.PLAN_MY_ESTATE,
      EstateStatus.AR_CONTACT,
      EstateStatus.AR_ASSESSMENT,
      EstateStatus.AR_GUIDANCE,
      EstateStatus.AR_DECISIONS,
      EstateStatus.AR_WILL_PREP,
      EstateStatus.AR_CLIENT_REVIEW,
      EstateStatus.AR_PENDING_SIGNATURES,
      EstateStatus.AR_UPLOADS,
      EstateStatus.AR_AFFIDAVIT_OF_EXECUTION,
      EstateStatus.AR_WAITING_ON_ORIGINALS,
      EstateStatus.AR_SS_COMPLETED,
      EstateStatus.AR_NO_CHANGES,
      EstateStatus.AR_GUIDANCE_CSM,
      EstateStatus.AR_GUIDANCE_EPA
    ];
  }

  //
  static getApprovedWillMilestoneStatusKeys(): string[] {
    return [
      EstateStatus.SIGNATURES.key,
      EstateStatus.UPLOADS.key,
      EstateStatus.COMPLETED.key,
      EstateStatus.MAIL_FOLDER.key,
      EstateStatus.AR_ASSESSMENT.key,
      EstateStatus.AR_GUIDANCE.key,
      EstateStatus.AR_DECISIONS.key,
      EstateStatus.AR_WILL_PREP.key,
      EstateStatus.AR_CLIENT_REVIEW.key,
      EstateStatus.AR_PENDING_SIGNATURES.key
    ];
  }
}
